import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { ArrowLeft, Download, Star } from "lucide-react";
import QRCode from "qrcode";
import { supabase } from "@/integrations/supabase/client";
import { useLanguage } from "@/i18n/LanguageContext";
import { Button } from "@/components/ui/button";

type Review = { id: string; author: string; photo: string | null; stars: number; comment: string; date: string };
type Data = { reviewUrl: string | null; mapsUrl: string | null; rating: number | null; count: number; reviews: Review[] };

const COPY = {
  fr: {
    title: "Avis clients | Enteleky BE",
    desc: "Partagez votre expérience avec Enteleky BE sur Google.",
    h1: "Votre avis compte",
    intro: "Vous avez travaillé avec nous ? Quelques mots sur Google aident d'autres entrepreneurs, médecins et avocats à nous trouver. Merci pour votre confiance.",
    cta: "Laisser un avis Google",
    pending: "Notre fiche Google est en cours de validation. Le lien d'avis sera disponible très bientôt.",
    qr: "Scannez pour laisser un avis",
    download: "Télécharger le QR code",
    reviews: "Ce que disent nos clients",
    empty: "Soyez le premier à laisser un avis.",
    count: (n: number) => `${n} avis`,
    back: "Retour",
  },
  en: {
    title: "Client reviews | Enteleky BE",
    desc: "Share your experience with Enteleky BE on Google.",
    h1: "Your review matters",
    intro: "Worked with us? A few words on Google help other entrepreneurs, doctors and lawyers find us. Thank you for your trust.",
    cta: "Leave a Google review",
    pending: "Our Google listing is being validated. The review link will be available very soon.",
    qr: "Scan to leave a review",
    download: "Download QR code",
    reviews: "What our clients say",
    empty: "Be the first to leave a review.",
    count: (n: number) => `${n} reviews`,
    back: "Back",
  },
};

const Stars = ({ n }: { n: number }) => (
  <span className="inline-flex gap-0.5" aria-label={`${n}/5`}>
    {[1, 2, 3, 4, 5].map((i) => (
      <Star key={i} size={16} className={i <= Math.round(n) ? "fill-gold text-gold" : "text-muted-foreground/40"} />
    ))}
  </span>
);

const Avis = () => {
  const { lang } = useLanguage();
  const c = COPY[lang === "en" ? "en" : "fr"];
  const [data, setData] = useState<Data | null>(null);
  const [qr, setQr] = useState<string | null>(null);

  useEffect(() => {
    supabase.functions.invoke("google-reviews").then(({ data }) => setData(data as Data));
  }, []);

  useEffect(() => {
    if (data?.reviewUrl) QRCode.toDataURL(data.reviewUrl, { width: 600, margin: 2 }).then(setQr);
  }, [data?.reviewUrl]);

  return (
    <div className="min-h-screen bg-background">
      <Helmet>
        <title>{c.title}</title>
        <meta name="description" content={c.desc} />
        <link rel="canonical" href="https://www.b-entelechy.eu/avis" />
      </Helmet>
      <div className="container mx-auto px-6 lg:px-12 py-12 max-w-4xl">
        <Link to="/" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-8">
          <ArrowLeft size={16} /> {c.back}
        </Link>

        <h1 className="text-3xl md:text-5xl font-bold text-foreground mb-4">{c.h1}</h1>
        <p className="text-lg text-muted-foreground max-w-2xl mb-10">{c.intro}</p>

        <div className="grid md:grid-cols-[1fr_auto] gap-10 items-center rounded-xl border border-border p-8 bg-card">
          <div className="space-y-4">
            {data?.reviewUrl ? (
              <Button asChild size="lg" className="min-h-11">
                <a href={data.reviewUrl} target="_blank" rel="noopener noreferrer">{c.cta}</a>
              </Button>
            ) : (
              <p className="text-muted-foreground">{data ? c.pending : "…"}</p>
            )}
            {data?.rating != null && (
              <div className="flex items-center gap-3 text-foreground">
                <span className="text-2xl font-semibold">{data.rating.toFixed(1)}</span>
                <Stars n={data.rating} />
                <span className="text-sm text-muted-foreground">{c.count(data.count)}</span>
              </div>
            )}
          </div>
          {qr && (
            <div className="flex flex-col items-center gap-3">
              <img src={qr} alt={c.qr} className="w-44 h-44 rounded-lg bg-background" />
              <span className="text-xs text-muted-foreground">{c.qr}</span>
              <Button asChild variant="outline" size="sm">
                <a href={qr} download="enteleky-be-avis-google.png"><Download size={14} /> {c.download}</a>
              </Button>
            </div>
          )}
        </div>

        <h2 className="text-2xl font-semibold text-foreground mt-14 mb-6">{c.reviews}</h2>
        {data && data.reviews.length === 0 && <p className="text-muted-foreground">{c.empty}</p>}
        <div className="grid gap-4 md:grid-cols-2">
          {data?.reviews.map((r) => (
            <article key={r.id} className="rounded-xl border border-border p-6 bg-card">
              <div className="flex items-center justify-between mb-3">
                <span className="font-medium text-foreground">{r.author}</span>
                <Stars n={r.stars} />
              </div>
              {r.comment && <p className="text-muted-foreground text-sm">{r.comment}</p>}
              <time className="block mt-3 text-xs text-muted-foreground/70">
                {new Date(r.date).toLocaleDateString(lang === "en" ? "en-GB" : "fr-BE")}
              </time>
            </article>
          ))}
        </div>
      </div>
    </div>
  );
};

export default Avis;
